// PersonRowActions — the people-lifecycle tabs' Actions cell: a kebab Dropdown
// whose items depend on the tab. People gets View profile (same as row click)
// + Archive (single-row ride on the bulk endpoint); Pending gets Accept +
// Reject (a `pending` request) or Delete (an `admin_invite` — withdraws it);
// Archived gets View + Restore + Delete, where Delete removes the person for
// real (deleting a person is archive-first by product decision, which is why
// the people variant has no Delete at all), and Restore is the single-row ride
// on the bulk endpoint that moves the person back to the People tab. Every
// write item is an OPTIONAL
// callback: the page passes `undefined` when the verb is missing and the item
// (with its divider) simply doesn't render — absence here means hidden. A
// pending row with no grant renders no kebab at all (it has no read item).
// Which pending items a row gets is the page's call (by row status) — this
// component only renders what it's handed.
// Owns its own open state (one row's menu at a time; outside-click closes);
// the flows live in PeoplePage via the callbacks.

import { useState, type MouseEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Icon, IconButton } from '@/components/common';

interface PersonRowActionsProps {
  variant: 'people' | 'pending' | 'archived';
  /** View profile — opens the Profile drawer in view mode. */
  onView?: () => void;
  /** Archive (people tab). Absent = hidden. */
  onArchive?: () => void;
  /** Accept a pending request (pending tab). Absent = hidden. */
  onAccept?: () => void;
  /** Reject a pending request (pending tab). Absent = hidden. */
  onReject?: () => void;
  /** Delete — withdraws the admin invite (pending) or removes the person
   *  (archived). Absent = hidden. */
  onDelete?: () => void;
  /** Restore (archived tab). Absent = hidden. */
  onRestore?: () => void;
}

const PersonRowActions = ({
  variant,
  onView,
  onArchive,
  onAccept,
  onReject,
  onDelete,
  onRestore,
}: PersonRowActionsProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const stop = (e: MouseEvent) => e.stopPropagation();

  // The pending kebab has no read item — with nothing handed in there is
  // nothing to open, so the cell stays empty rather than showing a dead trigger.
  if (variant === 'pending' && !onAccept && !onReject && !onDelete) return null;

  const item = (
    label: string,
    icon: string,
    onClick: (() => void) | undefined,
    opts: { danger?: boolean } = {},
  ): ReactNode => (
    <button
      type='button'
      className={`app-dropdown__item${opts.danger ? ' app-dropdown__item--danger' : ''}`}
      role='menuitem'
      onClick={() => {
        setOpen(false);
        onClick?.();
      }}
    >
      <Icon name={icon} size={15} />
      <span className='app-dropdown__item-label'>{label}</span>
    </button>
  );

  return (
    <span className='people-table__actions' onClick={stop}>
      <Dropdown
        open={open}
        onClose={() => setOpen(false)}
        align='end'
        width={220}
        portal
        trigger={
          <IconButton
            icon='ellipsis-vertical'
            variant='ghost'
            size='sm'
            aria-label={t('people_col_actions')}
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
          />
        }
      >
        {variant === 'people' ? (
          <>
            {item(t('people_menu_view_profile'), 'eye', onView)}
            {onArchive && (
              <>
                <div className='app-dropdown__divider' role='separator' />
                {item(t('people_bulk_archive'), 'archive', onArchive, { danger: true })}
              </>
            )}
          </>
        ) : variant === 'pending' ? (
          <>
            {onAccept && item(t('people_pending_accept'), 'user-check', onAccept)}
            {onReject && item(t('people_pending_reject'), 'user-x', onReject, { danger: true })}
            {onDelete && (
              <>
                {(onAccept || onReject) && (
                  <div className='app-dropdown__divider' role='separator' />
                )}
                {item(t('button_delete'), 'trash-2', onDelete, { danger: true })}
              </>
            )}
          </>
        ) : (
          <>
            {item(t('people_menu_view_profile'), 'eye', onView)}
            {onRestore && (
              <>
                <div className='app-dropdown__divider' role='separator' />
                {item(t('people_menu_restore'), 'archive-restore', onRestore)}
              </>
            )}
            {onDelete && (
              <>
                <div className='app-dropdown__divider' role='separator' />
                {item(t('button_delete'), 'trash-2', onDelete, { danger: true })}
              </>
            )}
          </>
        )}
      </Dropdown>
    </span>
  );
};

export default PersonRowActions;
